import React, { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Image as ImageIcon, Paperclip, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { fileSize } from "@/lib/format";

const blobCache = new Map();
async function blobUrl(pid, f) {
  const key = `${pid}/${f.id}`;
  if (blobCache.has(key)) return blobCache.get(key);
  const r = await api.get(`/inbox/${pid}/files/${f.id}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  blobCache.set(key, url);
  return url;
}

const isImg = (f) => (f.mime || "").startsWith("image/") || /\.(jpe?g|png|webp|heic)$/i.test(f.name || "");
const isSheet = (f) => f.kind === "sheet" || /\.(xlsx|xlsm|xls|csv)$/i.test(f.name || "");
const isPdf = (f) => /\.pdf$/i.test(f.name || "");
const label = (f) => (isImg(f) ? "Foto" : isSheet(f) ? "Regneark" : isPdf(f) ? "PDF" : "Fil");

function Photo({ pid, f }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { let alive = true; blobUrl(pid, f).then((u) => alive && setSrc(u)).catch(() => {}); return () => { alive = false; }; }, [pid, f]);
  return (
    <button type="button" onClick={() => src && window.open(src, "_blank", "noopener")} className="group relative block aspect-[4/3] w-full overflow-hidden rounded-[12px] border border-black/[0.06] bg-[hsl(210_20%_96%)] text-left" title={f.name} data-testid={`inbox-reader-file-${f.id || f.name}`}>
      {src ? <img src={src} alt={f.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" /> : <span className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></span>}
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-[12px] text-white"><span className="block truncate font-medium">{f.name}</span><span className="text-white/75">{f.size ? fileSize(f.size) : "Foto"}</span></span>
    </button>
  );
}

function Doc({ pid, f }) {
  const [busy, setBusy] = useState(false);
  const Icon = isSheet(f) ? FileSpreadsheet : isPdf(f) ? FileText : Paperclip;
  const open = async () => { setBusy(true); try { window.open(await blobUrl(pid, f), "_blank", "noopener"); } finally { setBusy(false); } };
  return (
    <button type="button" onClick={open} disabled={!f.id} className="group flex w-full items-center gap-3 rounded-[12px] border border-black/[0.06] bg-white px-3 py-2.5 text-left transition-[border-color,background-color] hover:border-black/[0.16] hover:bg-[hsl(210_20%_98.5%)] disabled:cursor-default" data-testid={`inbox-reader-file-${f.id || f.name}`}>
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", isSheet(f) ? "bg-[hsl(var(--brand-soft))] text-primary" : "bg-black/[0.05] text-muted-foreground")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}</span>
      <span className="min-w-0 flex-1 leading-tight"><span className="block truncate text-[13px] font-medium">{f.name}</span><span className="block text-[11.5px] text-muted-foreground">{[label(f), f.size ? fileSize(f.size) : null].filter(Boolean).join(" · ")}</span></span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

/** What the customer sent along: photos as photos, documents as a quiet list – each opens as the customer sent it. */
export function RequestAttachments({ p }) {
  const files = p.files || [];
  if (!files.length) return null;
  const photos = files.filter(isImg);
  const docs = files.filter((f) => !isImg(f));
  return (
    <section className="border-t border-black/[0.06] pt-5" data-testid="inbox-reader-files">
      <h3 className="text-[12.5px] font-semibold text-muted-foreground">Vedlegg · {files.length}</h3>
      {photos.length ? <div className={cn("mt-3 grid gap-2", photos.length === 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3")}>{photos.map((f) => <Photo key={f.id || f.name} pid={p.id} f={f} />)}</div> : null}
      {docs.length ? <ul className={cn("grid gap-2 sm:grid-cols-2", photos.length ? "mt-2" : "mt-3")}>{docs.map((f) => <li key={f.id || f.name}><Doc pid={p.id} f={f} /></li>)}</ul> : null}
    </section>
  );
}

/** The assistant's first reading of the request: one sentence, the hard facts, the areas of work and what is unclear. */
export function RequestRead({ p }) {
  const v = p.vessel || {};
  const sc = p.scope || {};
  const fx = p.facts || {};
  const facts = [["Fartøy", v.name], ["Type", v.type], ["Klasse", v.class_society], ["Bygget", v.built ? String(v.built) : null], ["Lengde", v.loa], ["Periode", fx.period_note || sc.period], ["Tilbudsfrist", fx.tender_deadline ? new Date(fx.tender_deadline).toLocaleDateString("nb-NO", { day: "numeric", month: "long" }) : null], ["Språk", sc.language === "en" ? "Engelsk" : null]].filter(([, val]) => val);
  if (!p.summary && !facts.length && !sc.systems?.length && !p.open_questions?.length) return null;
  return (
    <section className="border-t border-black/[0.06] pt-5" data-testid="inbox-reader-ai">
      <h3 className="text-[12.5px] font-semibold text-muted-foreground">Assistenten har lest</h3>
      <div className="mt-3 grid gap-6 md:grid-cols-[1.25fr_1fr]">
        <div>
          {p.summary ? <p className="text-[15px] leading-[1.65] text-foreground/90" data-testid="inbox-reader-summary">{p.summary}</p> : null}
          {sc.systems?.length ? <div className="mt-3 flex flex-wrap gap-1.5" data-testid="inbox-reader-areas">{sc.systems.map((s) => <span key={s} className="rounded-full bg-black/[0.05] px-2.5 py-1 text-[12px] font-medium text-foreground/80">{s}</span>)}</div> : null}
          {p.open_questions?.length ? (
            <div className="mt-5" data-testid="inbox-reader-questions">
              <div className="text-[12px] font-semibold text-foreground/60">Uklart i forespørselen</div>
              <ul className="mt-1.5 space-y-1.5 text-[14px] leading-[1.5] text-foreground/85">{p.open_questions.map((q) => <li key={q} className="flex gap-2"><span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-foreground/40" />{q}</li>)}</ul>
            </div>
          ) : null}
        </div>
        {facts.length ? <dl className="self-start divide-y divide-black/[0.05] text-[13.5px]">{facts.map(([k, val]) => <div key={k} className="flex justify-between gap-4 py-1.5"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{val}</dd></div>)}</dl> : null}
      </div>
    </section>
  );
}
